// Arquivo: src/hooks/useChat.js
// O servidor já filtra tudo por tenant. O hook apenas consome os eventos com segurança.
//
// CORREÇÕES aplicadas:
//   FIX 1 — Token passado no handshake do socket (auth: { token })
//            Sem isso, autoRegisterByToken no servidor falha e o usuário
//            nunca entra na sala tenant_X → todo mundo aparece offline.
//   FIX 2 — transports: ['websocket', 'polling']
//            No Render Starter o upgrade polling→websocket falha com frequência.
//            Forçar websocket primeiro elimina a instabilidade.
//   FIX 3 — newUser emitido com active_tenant_id extraído do token
//            Garante que o servidor coloque o usuário na sala correta mesmo
//            se o autoRegisterByToken já tiver rodado antes do newUser chegar.
//
//   ANEXOS VIA DRIVE — sendFileMessage()
//            Sobe o arquivo pela rota POST /api/chat/messages/anexo (multipart),
//            que já envia ao Google Drive e PERSISTE a mensagem. Por isso esta
//            função NÃO chama POST /api/chat/messages (evita gravar em dobro):
//            faz apenas o insert otimista local + emite o socket para o
//            destinatário receber em tempo real.

import { useEffect, useState, useRef, useCallback } from 'react';
import io from 'socket.io-client';
import api from '../services/api';

const SOCKET_URL =
    process.env.REACT_APP_SOCKET_URL ||
    process.env.REACT_APP_API_BASE_URL ||
    'http://localhost:3001';

// Extrai o tenant_id do JWT salvo no localStorage sem depender de libs externas
function extractTenantFromToken() {
    try {
        const raw = localStorage.getItem('token');
        if (!raw) return null;
        const decoded = JSON.parse(atob(raw.split('.')[1]));
        return (
            decoded?.active_tenant_id ??
            decoded?.tenantId         ??
            decoded?.tenant_id        ??
            decoded?.cliente_id       ??
            decoded?.clienteId        ??
            null
        );
    } catch {
        return null;
    }
}

export const useChat = (user, activeChatUserId, isChatOpen) => {
    const [onlineUsers,   setOnlineUsers]   = useState([]);
    const [allContacts,   setAllContacts]   = useState([]);
    const [messages,      setMessages]      = useState({});
    const [typingUsers,   setTypingUsers]   = useState({});
    const [unreadSenders, setUnreadSenders] = useState(new Set());

    const socket        = useRef(null);
    const activeChatRef = useRef(activeChatUserId);
    const isChatOpenRef = useRef(isChatOpen);

    // Mantém refs sincronizadas para uso dentro dos listeners do socket
    useEffect(() => { activeChatRef.current = activeChatUserId; }, [activeChatUserId]);
    useEffect(() => { isChatOpenRef.current = isChatOpen;       }, [isChatOpen]);

    // ── 1. Contatos ──────────────────────────────────────────────
    const fetchContacts = useCallback(async () => {
        if (!user?.id) return;
        try {
            const { data } = await api.get('/api/chat/contacts');
            setAllContacts(data);
        } catch (err) {
            console.error('[useChat] Erro ao carregar contatos:', err);
        }
    }, [user?.id]);

    // ── 2. Histórico de conversa ──────────────────────────────────
    useEffect(() => {
        if (!activeChatUserId || !user?.id) return;

        const fetchHistory = async () => {
            try {
                const { data } = await api.get(`/api/chat/conversation/${activeChatUserId}`);
                const formatted = data.map(msg => ({
                    id:         msg.id,
                    text:       msg.message,
                    senderId:   msg.sender_id,
                    receiverId: msg.receiver_id,
                    status:     msg.status,
                    timestamp:  msg.timestamp,
                    type:       msg.sender_id === user.id ? 'sent' : 'received',
                    file:       msg.file_data
                        ? (typeof msg.file_data === 'string'
                            ? JSON.parse(msg.file_data)
                            : msg.file_data)
                        : null,
                }));
                setMessages(prev => ({ ...prev, [activeChatUserId]: formatted }));
            } catch (err) {
                console.error('[useChat] Erro ao carregar histórico:', err);
            }
        };

        fetchHistory();
    }, [activeChatUserId, user?.id]);

    // ── 3. Não lidas ao iniciar ───────────────────────────────────
    useEffect(() => {
        if (!user?.id) return;
        const fetchUnread = async () => {
            try {
                const { data } = await api.get('/api/chat/unread-summary');
                const ids = new Set(data.map(item => item.sender_id ?? item.senderId));
                setUnreadSenders(ids);
            } catch (err) {
                console.error('[useChat] Erro ao carregar não lidas:', err);
            }
        };
        fetchUnread();
    }, [user?.id]);

    // ── 4. Socket listeners ───────────────────────────────────────
    useEffect(() => {
        if (!user?.id) return;

        // FIX 1 + FIX 2: cria socket COM token no handshake e transports corretos
        if (!socket.current) {
            socket.current = io(SOCKET_URL, {
                // FIX 2: WebSocket primeiro — evita instabilidade do Render com
                // o upgrade polling→websocket que trava em produção
                transports:           ['websocket', 'polling'],
                // Não desiste de reconectar (Render derruba socket com frequência).
                reconnectionAttempts: Infinity,
                reconnectionDelay:    2000,
                // AUTENTICAÇÃO POR COOKIE: manda o cookie HttpOnly blb_access no
                // handshake. O servidor (extractTokenFromHandshake) lê o cookie e
                // coloca o usuário na sala tenant_X. Substitui o antigo auth:{token},
                // que dependia do localStorage — agora vazio pós-migração (Fase 3).
                withCredentials:      true,
            });
        }

        const s = socket.current;

        // FIX 3: emite newUser com active_tenant_id para o servidor ter o tenant
        // explícito como fallback (caso autoRegisterByToken já tenha rodado)
        const tenantId = extractTenantFromToken();
        s.emit('newUser', {
            id:               user.id,
            active_tenant_id: tenantId,
        });

        fetchContacts();

        // [MULTITENANT] getUsers já vem filtrado pelo servidor — só do mesmo tenant
        const onGetUsers = (users) => setOnlineUsers(users);

        const onGetMessage = (data) => {
            const partnerId = data.senderId;

            setMessages(prev => {
                const chat = prev[partnerId] || [];
                // Evita duplicatas (ex: mensagem já persistida pelo sendMessage)
                if (chat.some(m => m.id === data.id)) return prev;
                return {
                    ...prev,
                    [partnerId]: [
                        ...chat,
                        { ...data, type: 'received', status: 'delivered' },
                    ],
                };
            });

            const isConversationActive =
                isChatOpenRef.current && partnerId === activeChatRef.current;

            if (isConversationActive) {
                // Usuário está vendo a conversa — marca como lida imediatamente
                s.emit('markAsRead', { conversationPartnerId: partnerId });
                api.put(`/api/chat/conversation/${partnerId}/read`).catch(() => {});
            } else {
                // Notificação de não lida — faz o FAB piscar
                setUnreadSenders(prev => new Set(prev).add(partnerId));
            }
        };

        // Atualiza status de leitura nas mensagens enviadas (confirmação de visto)
        const onMessagesRead = ({ readerId }) => {
            setMessages(prev => {
                const chat = prev[readerId];
                if (!chat) return prev;
                return {
                    ...prev,
                    [readerId]: chat.map(msg =>
                        msg.type === 'sent' ? { ...msg, status: 'read' } : msg
                    ),
                };
            });
        };

        const onTyping = ({ senderId, isTyping }) => {
            setTypingUsers(prev => ({ ...prev, [senderId]: isTyping }));
        };

        // [ONDA 2] Re-registro nas salas ao (re)conectar.
        // O servidor já identifica o usuário pelo cookie no handshake e o coloca
        // na sala driver_${id}. Mesmo assim reemitimos newUser como reforço para
        // garantir a reentrada imediata na sala após uma reconexão (o Render
        // derruba socket com frequência). O tenant real vem do cookie no servidor;
        // extractTenantFromToken hoje retorna null (localStorage vazio) e serve
        // apenas de fallback legado, sem efeito colateral.
        const reRegister = () => {
            const newTenantId = extractTenantFromToken();
            s.emit('newUser', {
                id:               user.id,
                active_tenant_id: newTenantId,
            });
            fetchContacts();
        };

        s.on('getUsers',     onGetUsers);
        s.on('getMessage',   onGetMessage);
        s.on('messagesRead', onMessagesRead);
        s.on('typing',       onTyping);

        // [ONDA 2 - FIX] No Socket.IO v4 o evento 'reconnect' vive no MANAGER
        // (s.io), não no socket. O antigo s.on('reconnect', ...) NUNCA disparava,
        // então após uma queda o usuário reconectava mas ficava fora da sala e
        // parava de receber mensagens até dar refresh. Agora:
        //   - s.io.on('reconnect'): re-registra após reconexão real;
        //   - s.on('connect'): cobre a conexão inicial e as subsequentes de forma
        //     robusta (dispara sempre que o socket conecta/reconecta de fato).
        s.on('connect', reRegister);
        s.io.on('reconnect', reRegister);

        return () => {
            s.off('getUsers',     onGetUsers);
            s.off('getMessage',   onGetMessage);
            s.off('messagesRead', onMessagesRead);
            s.off('typing',       onTyping);
            s.off('connect',      reRegister);
            s.io.off('reconnect', reRegister);
        };
    }, [user?.id, fetchContacts]);
    // Nota: activeChatUserId e isChatOpen são lidos via ref dentro dos callbacks,
    // evitando re-registrar listeners a cada mudança de conversa.

    // ── 5. Ações ──────────────────────────────────────────────────

    const sendMessage = useCallback(async ({ senderId, receiverId, text, file }) => {
        if (!receiverId || (!text?.trim() && !file)) return;

        const msgData = {
            id:         Date.now(),
            senderId,
            receiverId,
            text,
            file:       file || null,
            status:     'delivered',
            type:       'sent',
            timestamp:  new Date().toISOString(),
        };

        // Otimista: adiciona localmente antes da confirmação do servidor
        setMessages(prev => ({
            ...prev,
            [receiverId]: [...(prev[receiverId] || []), msgData],
        }));

        // Envia via socket (entrega em tempo real)
        socket.current?.emit('sendMessage', msgData);

        // Persiste no banco via REST
        try {
            await api.post('/api/chat/messages', {
                receiverId,
                message:  text,
                fileData: file ? JSON.stringify(file) : null,
            });
        } catch (err) {
            console.error('[useChat] Erro ao persistir mensagem:', err);
        }
    }, []);

    // Envio de ANEXO — sobe o arquivo pela rota /messages/anexo (multipart → Drive).
    // Essa rota JÁ persiste a mensagem; aqui só fazemos o insert otimista e o
    // broadcast por socket. Retorna a mensagem criada (ou null em caso de erro).
    const sendFileMessage = useCallback(async ({ senderId, receiverId, file, caption }) => {
        if (!receiverId || !file) return null;

        try {
            const form = new FormData();
            form.append('file', file);
            form.append('receiverId', receiverId);
            if (caption && caption.trim()) form.append('message', caption.trim());

            const { data } = await api.post('/api/chat/messages/anexo', form, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            // data => { id, sender_id, receiver_id, message, status, file: {kind,driveFileId,fileName,mimeType} }
            const msgData = {
                id:         data.id,
                senderId,
                receiverId,
                text:       data.message,
                file:       data.file || null,
                status:     data.status || 'delivered',
                type:       'sent',
                timestamp:  new Date().toISOString(),
            };

            // Insert otimista/local (dedup por id)
            setMessages(prev => {
                const chat = prev[receiverId] || [];
                if (chat.some(m => m.id === msgData.id)) return prev;
                return { ...prev, [receiverId]: [...chat, msgData] };
            });

            // Entrega em tempo real ao destinatário (NÃO re-persiste — a rota já gravou)
            socket.current?.emit('sendMessage', msgData);

            return msgData;
        } catch (err) {
            console.error('[useChat] Erro ao enviar anexo:', err);
            return null;
        }
    }, []);

    const markMessagesAsRead = useCallback(({ conversationPartnerId }) => {
        if (!conversationPartnerId) return;
        socket.current?.emit('markAsRead', { conversationPartnerId });
        api.put(`/api/chat/conversation/${conversationPartnerId}/read`).catch(() => {});
        setUnreadSenders(prev => {
            const next = new Set(prev);
            next.delete(conversationPartnerId);
            return next;
        });
    }, []);

    const toggleFavorite = useCallback(async (favoriteId) => {
        try {
            const { data } = await api.post('/api/chat/favorites/toggle', { favoriteId });
            setAllContacts(prev =>
                prev.map(c =>
                    (c.userId === favoriteId || c.id === favoriteId)
                        ? { ...c, isFavorite: data.isFavorite ? 1 : 0 }
                        : c
                )
            );
        } catch (err) {
            console.error('[useChat] Erro ao alternar favorito:', err);
        }
    }, []);

    const startTyping = useCallback((receiverId) => {
        if (receiverId) socket.current?.emit('startTyping', { receiverId });
    }, []);

    const stopTyping = useCallback((receiverId) => {
        if (receiverId) socket.current?.emit('stopTyping', { receiverId });
    }, []);

    return {
        onlineUsers,
        allContacts,
        messages,
        typingUsers,
        unreadSenders,
        sendMessage,
        sendFileMessage,
        markMessagesAsRead,
        toggleFavorite,
        startTyping,
        stopTyping,
    };
};